import { useCallback } from 'react';

import useDebouncedCallback from '~hooks/useDebouncedCallback';
import { IEntityComponentProps } from '~interfaces/props';
import { convertToRawValue } from '~services/data-conversion';
import { AnnotationsList } from './AnnotationsList';
import { Annotation, annotationToRaw, rawToAnnotation } from './AnnotationUtils';

export type AnnotationsListContainerProps = {
  editing: boolean;
  entity: Entity;
  onChange: IEntityComponentProps['onChange'];
  onMouseOver: (id: string) => void;
  onMouseOut: (id: string) => void;
  onClick: (id: string) => void;
  focusMarkerId: string;
  items: Annotation[];
};

export const AnnotationsListContainer = ({
  editing,
  entity,
  onChange,
  focusMarkerId,
  onMouseOut,
  onMouseOver,
  onClick,
  items,
}: AnnotationsListContainerProps) => {
  const handleChangeItem = useDebouncedCallback((name: string, value: string) => {
    try {
      if (editing) {
        const newValue = convertToRawValue(value);
        const sanitizedNewValue = newValue.replace(/\n/g, '');
        const idx = parseInt(name.substr(name.lastIndexOf('.') + 1), 10);
        const rawAnnotation: string = entity.attributes.field_annotations_items[idx];
        const anno = rawToAnnotation(rawAnnotation);
        if (sanitizedNewValue !== anno.text) {
          onChange(`attributes.field_annotations_items.${idx}`, annotationToRaw({ ...anno, text: sanitizedNewValue }));
        }
      }
    } catch (_) {
      /* do nothing */
    }
  }, 350);

  const handleDeleteItem = useCallback(
    (index: number) => {
      try {
        if (editing) {
          onChange(`attributes.field_annotations_items`, null, false, index);
        }
      } catch (_) {
        /* do nothing */
      }
    },
    [editing, onChange],
  );

  return items ? (
    <AnnotationsList
      items={items}
      editing={editing}
      onChange={handleChangeItem}
      onDelete={handleDeleteItem}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      onClick={onClick}
      focusItemId={focusMarkerId}
    />
  ) : null;
};
